import { inject, EnvironmentInjector } from '@angular/core';
import { Named } from 'app/model/base/named';
import { LoggerService, IModelChanged } from 'app/logger/logger.service';
import { ModelMarker, eModelState } from 'app/model/services/state';
import { ModelChangeService } from 'app/model/services/model-change.service';
import { UndoRedoService } from 'app/model/services/undo-redo.service';
import { GlobalVariables, ResourceType } from 'app/services/globals';
import { Metamodel } from 'app/model/base/model';

export { markBroken, markListed, markProxy, markZombie } from 'shell-models-lib';

/**
 * todo: Suggest make buildModelMarkerWrapperInContext into a ModelMarkerWrapperService
 * if we could decoupled it with other utils.
 */
export function buildModelMarkerWrapperInContext() {

    const environmentInjector = inject(EnvironmentInjector);

    return {
        bulkMark(entities: Named[], state: eModelState, erase?: boolean){
            const undoRedoService = environmentInjector.get(UndoRedoService);
            const loggerService = environmentInjector.get(LoggerService);
            const modelChangeSvc = environmentInjector.get(ModelChangeService);

            const changes: IModelChanged[] = [];
            entities.forEach((entity) => {
                let stateChangeType: 'Create' | 'Update' | 'Fetched' | 'Delete';
                switch (state) {
                    case eModelState.DIRTY:
                        ModelMarker.markDirty(entity, erase);
                        undoRedoService.addUndoState(entity);
                        stateChangeType = 'Update';
                        break;
                    case eModelState.NEW:
                        ModelMarker.markNew(entity);
                        stateChangeType = 'Create';
                        break;
                    case eModelState.LISTED:
                        ModelMarker.markListed(entity);
                        break;
                    case eModelState.FETCHED:
                        ModelMarker.markFetched(entity);
                        undoRedoService.clearUndoRedoHistory(entity);
                        stateChangeType = 'Fetched';
                        break;
                    case eModelState.DELETED:
                        ModelMarker.markDelete(entity);
                        undoRedoService.clearUndoRedoHistory(entity);
                        stateChangeType = 'Delete';
                        break;
                    case eModelState.PROXY:
                        ModelMarker.markProxy(entity);
                        break;
                    case eModelState.ZOMBIE:
                        ModelMarker.markZombie(entity);
                        break;
                    case eModelState.BROKEN:
                        ModelMarker.markBroken(entity);
                        break;
                    case eModelState.SAVED:
                        ModelMarker.markSaved(entity, erase);
                        break;
                    default:
                    // do nothing
                }
                const change: IModelChanged = {
                    changeType: stateChangeType,
                    named: entity
                };
                changes.push(change);
            });
            loggerService.modelChanged$.next(changes);
            modelChangeSvc.modelChanged$.next(changes);
        },
        markDirty(entity: Named, canUndoRedo?: boolean, isRename?: boolean){
            const undoRedoService = environmentInjector.get(UndoRedoService);
            const loggerService = environmentInjector.get(LoggerService);
            const modelChangeSvc = environmentInjector.get(ModelChangeService);

            const modelAlreadyDirty = ModelMarker.isDirty(entity);
            if (canUndoRedo) {
                undoRedoService.addUndoState(entity);
            }
            ModelMarker.markDirty(entity);

            // mark as unsaved for autosave
            if (GlobalVariables.enableAutosave) {
                const concept = Metamodel.modelOf(entity).concept;
                const conceptName = concept ? concept.name : '';
                if (GlobalVariables.isAutosaveSupportType(conceptName as ResourceType)) {
                    ModelMarker.markUnsaved(entity);
                }
            }

            const change: IModelChanged = {
                changeType: 'Update',
                named: entity
            };

            if (isRename) {
                change.subChangeType = 'rename';
            }

            if (!modelAlreadyDirty) {
                loggerService.modelChanged$.next([change]);
            }
            modelChangeSvc.modelChanged$.next([change]);
        },
        markNew(entity: Named, lifeline?: Named){
            const loggerService = environmentInjector.get(LoggerService);
            const modelChangeSvc = environmentInjector.get(ModelChangeService);

            ModelMarker.markNew(entity, lifeline);
            const change: IModelChanged = {
                changeType: 'Create',
                named: entity
            };
            loggerService.modelChanged$.next([change]);
            modelChangeSvc.modelChanged$.next([change]);
        },
        markDelete(entity: Named, canUndoRedo?: boolean){
            const undoRedoService = environmentInjector.get(UndoRedoService);
            const loggerService = environmentInjector.get(LoggerService);
            const modelChangeSvc = environmentInjector.get(ModelChangeService);

            ModelMarker.markDelete(entity);
            if (canUndoRedo) {
                undoRedoService.clearUndoRedoHistory(entity);
            }
            const change: IModelChanged = {
                changeType: 'Delete',
                named: entity
            };
            loggerService.modelChanged$.next([change]);
            modelChangeSvc.modelChanged$.next([change]);
        },
        markFetched(entity: Named){
            const undoRedoService = environmentInjector.get(UndoRedoService);
            const loggerService = environmentInjector.get(LoggerService);
            const modelChangeSvc = environmentInjector.get(ModelChangeService);

            undoRedoService.clearUndoRedoHistory(entity);
            ModelMarker.markFetched(entity);
            const change: IModelChanged = {
                changeType: 'Fetched',
                named: entity
            };
            loggerService.modelChanged$.next([change]);
            modelChangeSvc.modelChanged$.next([change]);
        }
    };
}

export type ModelMarkerWrapper = ReturnType<typeof buildModelMarkerWrapperInContext>;
